/* eslint-disable camelcase */

import { queryClient } from 'config/ReactQueryClientProvider';
import { environment } from 'config/environment';
import {
  authTokenSchema,
  authTokenDTOSchema,
  LoginDTO,
  SendRemindPwdEmailDTO,
  AuthTokenDTO,
  AuthToken,
} from 'pages/auth/authModels';
import { authQueryKeys } from 'pages/auth/authQueryKeys';
import { pipe } from 'rambda';
import { BaseAxios, flatten } from 'services/axios';
import dayjs from 'dayjs';

const getTokenFromStorage = () => {
  const lsTokenData = localStorage.getItem(authQueryKeys.token[0]);

  return lsTokenData ? authTokenSchema.parse(JSON.parse(lsTokenData)) : null;
};

const saveTokenToStorageAndQuery = (token: AuthToken) => {
  queryClient.setQueriesData(authQueryKeys.token, token);
  localStorage.setItem(authQueryKeys.token[0], JSON.stringify(token));
};

const transformAuthToken = (token: AuthTokenDTO) => ({
  ...token,
  expires_at: dayjs().add(token.expires_in, 's'),
});

const login = ({ username, password }: LoginDTO) => {
  const body = {
    grant_type: environment.auth.grantType,
    username,
    password,
    audience: environment.auth.audience,
    client_id: environment.auth.clientId,
    realm: environment.auth.realm,
    scope: 'offline_access openid profile',
  };

  return BaseAxios.post<AuthTokenDTO>(
    `${environment.auth.url}/oauth/token`,
    new URLSearchParams(body)
  ).then(pipe(flatten, transformAuthToken));
};

const logout = () => {
  localStorage.removeItem(authQueryKeys.token[0]);
  queryClient.resetQueries(authQueryKeys.token, { exact: true });
  queryClient.clear();
};

const refreshToken = () => {
  const lsTokenData = localStorage.getItem(authQueryKeys.token[0]);

  const token = lsTokenData
    ? authTokenDTOSchema.parse(JSON.parse(lsTokenData))
    : undefined;

  if (!token) {
    return Promise.reject('No refresh token found');
  }

  const body = {
    grant_type: 'refresh_token',
    client_id: environment.auth.clientId,
    refresh_token: `${token.refresh_token}`,
  };

  return BaseAxios.post<AuthTokenDTO>(
    `${environment.auth.url}/oauth/token`,
    new URLSearchParams(body)
  ).then(pipe(flatten, transformAuthToken));
};

const sendResetPwdEmail = ({ email }: SendRemindPwdEmailDTO) => {
  const body = {
    email,
    connection: environment.auth.realm,
    client_id: environment.auth.clientId,
  };

  return BaseAxios.post<string>(
    `${environment.auth.url}/dbconnections/change_password`,
    body
  );
};

export const authService = {
  login,
  refreshToken,
  logout,
  getTokenFromStorage,
  saveTokenToStorageAndQuery,
  sendResetPwdEmail,
};
